import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Upload, AlertCircle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { resumableUpload } from "@/lib/resumableUpload";

export type EvidenceUploadMode = "evidence" | "vault";

interface ComplianceItemMeta {
  id: string;
  title: string;
}

interface EvidenceUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unaId: string;
  mode: EvidenceUploadMode;
  complianceItem?: ComplianceItemMeta;
  onUploaded?: (documentId: string) => void;
}

const ACCEPT =
  ".pdf,application/pdf,.png,.jpg,.jpeg,image/png,image/jpeg,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
// 100 MB — accommodates full scanned Constitutions and packets
const MAX_BYTES = 100 * 1024 * 1024;

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-z0-9.\-]+/gi, "-").replace(/-+/g, "-").toLowerCase();
}

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function EvidenceUploadDialog({
  open,
  onOpenChange,
  unaId,
  mode,
  complianceItem,
  onUploaded,
}: EvidenceUploadDialogProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitle(complianceItem?.title ?? "");
      setFile(null);
      setProgress(0);
      setUploadedBytes(0);
      setErrorMsg(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, complianceItem?.title]);

  const runUpload = async () => {
    if (!file) {
      toast.error("Please choose a file to upload.");
      return;
    }
    if (!title.trim()) {
      toast.error("Please add a short title so this document is easy to find.");
      return;
    }
    if (file.size > MAX_BYTES) {
      const msg = `File is ${formatMB(file.size)}. The maximum is 100 MB. Try compressing or splitting the PDF.`;
      setErrorMsg(msg);
      toast.error(msg);
      return;
    }

    setUploading(true);
    setErrorMsg(null);
    setProgress(0);
    setUploadedBytes(0);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You must be signed in.");

      const uuid = crypto.randomUUID();
      const safeName = sanitizeFilename(file.name) || "upload";
      const path = `member-uploads/${unaId}/${uuid}-${safeName}`;

      // Resumable upload — chunks the file so large PDFs on slow connections
      // don't hit browser or gateway timeouts.
      await resumableUpload({
        bucket: "documents",
        path,
        file,
        contentType: file.type || "application/octet-stream",
        upsert: false,
        signal: controller.signal,
        onProgress: (pct, bytes) => {
          setProgress(pct);
          setUploadedBytes(bytes);
        },
      });

      const docType = mode === "vault" ? "vault" : "uploaded_evidence";
      const { data: docRow, error: insErr } = await supabase
        .from("generated_documents")
        .insert({
          una_id: unaId,
          document_name: title.trim(),
          document_type: docType,
          file_path: path,
          file_url: path,
          file_size: file.size,
          status: "final",
          is_custom: true,
          uploaded_filename: file.name,
          created_by: user.id,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;

      // Stamp storage owner so private-bucket owner-based policies pass.
      // Non-fatal — the resumable upload path already sets owner via the JWT.
      supabase
        .rpc("set_storage_object_owner", {
          p_bucket: "documents",
          p_path: path,
          p_owner: user.id,
        })
        .then(() => undefined, () => undefined);

      if (mode === "evidence" && complianceItem) {
        const { error: satErr } = await supabase.rpc("validate_and_satisfy_item", {
          _item_id: complianceItem.id,
          _evidence_type: "upload",
          _evidence_id: docRow.id,
        });
        if (satErr) throw satErr;

        // Recompute score in the background — never block the success toast.
        supabase
          .rpc("compute_una_governance_score", {
            _una_id: unaId,
            _period_year: new Date().getFullYear(),
          })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["gh-score", unaId] });
          }, () => undefined);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["gh-items", unaId] }),
        queryClient.invalidateQueries({ queryKey: ["gh-next-items", unaId] }),
        queryClient.invalidateQueries({ queryKey: ["vault-docs", unaId] }),
      ]);

      toast.success("Added to your record.");
      onUploaded?.(docRow.id);
      onOpenChange(false);
    } catch (e: any) {
      console.error("Evidence upload failed", e);
      const msg = e?.message || "Upload failed. Please try again.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    if (uploading && abortRef.current) {
      abortRef.current.abort();
      return;
    }
    onOpenChange(false);
  };

  const heading = mode === "vault" ? "Add to The Record" : "Upload Evidence";
  const intro =
    mode === "vault"
      ? "Keep a copy of an important document in The Record. Signed or stamped paper scans are welcome."
      : "Attach a file that shows this item is done. A signed or stamped scan works well.";


  return (
    <Dialog open={open} onOpenChange={(o) => (uploading ? null : onOpenChange(o))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>{intro}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="evidence-title">Document title</Label>
            <Input
              id="evidence-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Banking Authority Resolution, signed"
              disabled={uploading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="evidence-file">
              File (PDF, image, or DOCX, up to 100 MB)
            </Label>
            <Input
              id="evidence-file"
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setErrorMsg(null);
                setProgress(0);
              }}
              disabled={uploading}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {formatMB(file.size)}
              </p>
            )}
          </div>

          {uploading && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-xs text-muted-foreground">
                Uploading… {progress}%
                {file ? ` (${formatMB(uploadedBytes)} of ${formatMB(file.size)})` : ""}
              </p>
            </div>
          )}

          {errorMsg && !uploading && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Upload failed</div>
                <div className="text-xs mt-1">{errorMsg}</div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel} disabled={false}>
            {uploading ? "Cancel upload" : "Close"}
          </Button>
          <Button onClick={runUpload} disabled={uploading || !file || !title.trim()}>
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : errorMsg ? (
              <>
                <RotateCw className="w-4 h-4 mr-2" />
                Retry upload
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
